import styled, { css } from 'styled-components';

import { getCDNImageUrl } from '@/helpers/cdn';
import { GameItemMode } from '@/helpers/enums';

import { device, hexToRGB } from '@/styles/helpers';
const playSmIcon = getCDNImageUrl('/playSmIcon.svg');

export enum GameItemSize {
  L = 'L',
  M = 'M',
  S = 'S',
  XS = 'XS',
  XXS = 'XXS',
}

export interface ScGameItemWrapperProps {
  primeGame?: boolean;
}

export enum ColorMode {
  BLACK_BG = 'black_bg',
  GRAY_BG = 'gray_bg',
  WHITE_BG = 'white_bg',
}

export interface ScGamesGridItemProps {
  isAuthenticated?: boolean;
  isLocked?: boolean;
  isVisibleInfo?: boolean;
  gradientColor?: string;
  primeGame?: boolean;
  mode?: GameItemMode;
  size?: GameItemSize;
  isFavorite?: boolean;
  colorMode?: ColorMode;
}

export const ScGameItemWrapper = styled.div<ScGameItemWrapperProps>`
  width: 100%;
  display: ${({ primeGame }) => (primeGame ? `flex` : `block`)};
  flex-direction: column;
  align-items: center;

  @media (${device.mobile}) {
    flex-direction: row;
  }
`;

export const ScGamesGridItem = styled.div<ScGamesGridItemProps>`
  width: 100%;
  padding-top: 100%;
  position: relative;
  border-radius: 12px;
  overflow: hidden;
  z-index: 1;
  -webkit-mask-image: -webkit-radial-gradient(white, black);
  background-color: ${({ theme }) => hexToRGB(theme.whiteColor, 0.3)};
  color: ${({ theme }) => theme.whiteColor};

  @media (${device.mobile}) {
    ${({ primeGame }) =>
      primeGame &&
      css`
        flex: 0 0 148px;
        padding-top: 148px;
      `}
  }

  ${({ gradientColor, mode, size }) =>
    mode !== GameItemMode.jackpot &&
    mode !== GameItemMode.random &&
    size !== GameItemSize.XXS &&
    css`
      &::after {
        content: '';
        position: absolute;
        display: block;
        left: 0;
        right: 0;
        bottom: 0;
        height: 18%;
        background: rgb(2, 0, 36);
        background: ${gradientColor
          ? `linear-gradient(0deg, ${hexToRGB(
              gradientColor,
              0.8
            )} 0%, rgba(0, 0, 0, 0) 100%)`
          : `linear-gradient(0deg, rgba(0, 0, 0, 0.8) 0%, rgba(0, 0, 0, 0) 100%)`};

        @media (${device.mobileXS}) {
          height: 40px;
        }
      }
    `}

  ${({ mode, colorMode, size, theme }) =>
    mode === GameItemMode.jackpot &&
    css`
      &::after {
        content: '';
        position: absolute;
        display: block;
        left: 0;
        right: 0;
        bottom: 0;
        background: ${() => {
          switch (colorMode) {
            case ColorMode.GRAY_BG:
              return theme.blackColor;
            case ColorMode.BLACK_BG:
            case ColorMode.WHITE_BG:
            default:
              return theme.grayDarkColor;
          }
        }};

        ${() => {
          switch (size) {
            case GameItemSize.L:
              return css`
                height: 40px;
              `;
            case GameItemSize.M:
              return css`
                height: 32px;
              `;
            case GameItemSize.S:
            case GameItemSize.XS:
            case GameItemSize.XXS:
              return css`
                height: 28px;
              `;
          }
        }}
      }
    `}

  .jackpot-gradient {
    z-index: 2;
    position: absolute;
    width: 100%;
    left: 0;
    right: 0;
    background: linear-gradient(
      0deg,
      rgba(0, 0, 0, 0.8) 0%,
      rgba(0, 0, 0, 0) 100%
    );

    ${({ size }) => {
      switch (size) {
        case GameItemSize.L:
          return css`
            height: 40px;
            bottom: 40px;
          `;
        case GameItemSize.M:
          return css`
            bottom: 32px;
            height: 30px;
          `;
        case GameItemSize.S:
          return css`
            height: 24px;
            bottom: 28px;
          `;
        case GameItemSize.XS:
        case GameItemSize.XXS:
          return css`
            height: 22px;
            bottom: 28px;
          `;
      }
    }}
  }

  .badges {
    transform-origin: left top;
    position: absolute;
    left: 0;
    top: 0;
    display: grid;
    justify-items: start;
    background: ${({ theme, colorMode }) => {
      switch (colorMode) {
        case ColorMode.WHITE_BG:
          return theme.whiteColor;
        case ColorMode.GRAY_BG:
          return theme.grayLightColor;
        case ColorMode.BLACK_BG:
        default:
          return theme.blackColor;
      }
    }};
    border-radius: 0 0 12px 0;
    overflow: hidden;
    padding: 8px;
    row-gap: 12px;
    transition: opacity 0.3s;

    ${({ size }) => {
      switch (size) {
        case GameItemSize.L:
        case GameItemSize.M:
          return css`
            width: 40px;
          `;
        case GameItemSize.S:
        case GameItemSize.XS:
        case GameItemSize.XXS:
        default:
          return css`
            width: 32px;
            padding: 4px;
          `;
      }
    }}
  }

  & > .gradient {
    position: absolute;
    bottom: -1px;
    width: 100%;
  }

  & > .provider {
    text-transform: uppercase;
    position: absolute;
    text-align: center;
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    letter-spacing: 0.1em;
    line-height: 14px;
    font-size: 10px;
    transition: opacity 0.3s;
    z-index: 2;

    ${({ size, mode }) => {
      switch (size) {
        case GameItemSize.L:
        case GameItemSize.M:
          return css`
            bottom: ${mode === GameItemMode.jackpot ? '6px' : '8px'};
          `;
        case GameItemSize.S:
        case GameItemSize.XS:
        case GameItemSize.XXS:
        default:
          return css`
            bottom: ${mode === GameItemMode.jackpot ? '6px' : '4px'};
          `;
      }
    }}

    ${({ size, mode }) => {
      if (mode === GameItemMode.jackpot)
        switch (size) {
          case GameItemSize.L:
            return css`
              row-gap: 16px;
            `;
          case GameItemSize.M:
            return css`
              row-gap: 14px;
            `;
          case GameItemSize.S:
            return css`
              row-gap: 11px;
            `;
          case GameItemSize.XS:
          case GameItemSize.XXS:
          default:
            return css`
              row-gap: 9px;
            `;
        }
    }}

    .cup-icon {
      margin-right: 4px;
      ${({ size }) => {
        switch (size) {
          case GameItemSize.L:
          case GameItemSize.M:
            return css`
              width: 18px;
              height: 18px;
            `;
          case GameItemSize.S:
          case GameItemSize.XS:
          case GameItemSize.XXS:
          default:
            return css`
              width: 14px;
              height: 14px;
            `;
        }
      }}
    }

    .prize {
      flex-shrink: 0;
      font-weight: 700;

      ${({ size }) => {
        switch (size) {
          case GameItemSize.L:
            return css`
              line-height: 1.43;
              letter-spacing: 0.01em;
              font-size: 14px;
            `;
          case GameItemSize.M:
            return css`
              font-size: 12px;
              line-height: 18px;
            `;
          case GameItemSize.S:
          case GameItemSize.XS:
          case GameItemSize.XXS:
          default:
            return css`
              font-size: 10px;
              line-height: 14px;
            `;
        }
      }}
    }
  }

  & > .image {
    position: absolute;
    top: 0;
    left: 0;
    object-fit: cover;
    width: 100%;
    height: 100%;
    transition: 0.3s;
    border-radius: ${({ mode }) => (mode === GameItemMode.jackpot ? 0 : '8px')};
    backface-visibility: hidden;
    -webkit-backface-visibility: hidden;
    -moz-backface-visibility: hidden;
    -o-backface-visibility: hidden;

    ${({ mode }) =>
      mode === GameItemMode.jackpot &&
      css`
        height: 84%;
      `}

    ${({ mode, size }) => {
      if (mode === GameItemMode.jackpot) {
        switch (size) {
          case GameItemSize.L:
            return css`
              height: calc(100% - 40px);
            `;
          case GameItemSize.M:
            return css`
              height: calc(100% - 32px);
            `;
          case GameItemSize.S:
          case GameItemSize.XS:
          case GameItemSize.XXS:
          default:
            return css`
              height: calc(100% - 28px);
            `;
        }
      }
    }}
  }

  & > .currency-block {
    position: absolute;
    inset: 0;
    gap: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    background: ${({ theme }) => hexToRGB(theme.blackColor, 0.8)};
    z-index: 2;
    width: 100%;
    border-radius: 12px;
    padding: 16px;

    ${({ size }) => {
      switch (size) {
        case GameItemSize.L:
          return css`
            padding: 32px;
            gap: 24px;
          `;
        case GameItemSize.M:
          return css`
            gap: 16px;
          `;
        case GameItemSize.S:
          return css`
            gap: 12px;
          `;
        case GameItemSize.XS:
        case GameItemSize.XXS:
        default:
          return css`
            gap: 8px;
          `;
      }
    }}

    .lock-container {
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 50%;
      background: rgba(255, 255, 255, 0.2);
      justify-items: center;
      ${({ size }) => {
        switch (size) {
          case GameItemSize.L:
            return css`
              width: 64px;
              min-height: 64px;
              svg {
                width: 21px;
              }
            `;
          case GameItemSize.M:
            return css`
              width: 56px;
              min-height: 56px;
              svg {
                width: 21px;
              }
            `;
          case GameItemSize.S:
            return css`
              width: 48px;
              min-height: 48px;
              svg {
                width: 18px;
              }
            `;
          case GameItemSize.XS:
          case GameItemSize.XXS:
          default:
            return css`
              width: 32px;
              min-height: 32px;
              svg {
                width: 12px;
              }
            `;
        }
      }}
    }

    p {
      font-weight: bold;
      text-align: center;
      margin-bottom: 0;

      strong {
        color: ${({ theme }) => theme.secondaryColor};
      }
      ${({ size }) => {
        switch (size) {
          case GameItemSize.L:
          case GameItemSize.M:
            return css`
              font-size: 16px;
              line-height: 1.5;
            `;
          case GameItemSize.S:
          case GameItemSize.XS:
            return css`
              font-size: 14px;
              line-height: 1.43;

              &.de {
                font-size: 10px;
              }
            `;
          case GameItemSize.XXS:
          default:
            return css`
              font-size: 12px;
              line-height: 1.33;

              &.de {
                font-size: 10px;
              }
            `;
        }
      }}

      @media (max-width: 360px) {
        font-size: 10px;
      }
    }
  }

  @media (hover: hover) {
    &:hover {
      &::before {
        opacity: 1;
      }

      & > .gradient,
      & > .provider {
        opacity: 0;
      }
    }
  }

  ${({ isLocked }) =>
    isLocked &&
    css`
      @media (hover: hover) and (pointer: fine) {
        &:hover > .provider {
          opacity: 1;
        }
      }
    `}

  ${({ isVisibleInfo }) =>
    isVisibleInfo &&
    css`
      & > .badges,
      & > .provider {
        opacity: 0;
      }
    `}
`;

interface ScInfoBlockProps {
  primeGame?: boolean;
  size?: GameItemSize;
}

export const ScInfoBlock = styled.div<ScInfoBlockProps>`
  z-index: 2;
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: ${({ theme }) => hexToRGB(theme.blackColor, 0.9)};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  text-align: center;

  ${({ size }) => {
    switch (size) {
      case GameItemSize.L:
        return css`
          padding: 0 32px;
          gap: 24px;
        `;
      case GameItemSize.M:
        return css`
          padding: 0 16px;
          gap: 16px;
        `;
      case GameItemSize.S:
      case GameItemSize.XS:
        return css`
          padding: 22px 16px 0;
          gap: 8px;
        `;
      case GameItemSize.XXS:
      default:
        return css`
          padding: 0 16px;
          gap: 7px;
        `;
    }
  }}

  .title {
    letter-spacing: 0.01em;
    font-weight: bold;
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
    width: 100%;

    ${({ size }) => {
      switch (size) {
        case GameItemSize.L:
        case GameItemSize.M:
          return css`
            font-size: 16px;
            line-height: 24px;
          `;
        case GameItemSize.S:
        case GameItemSize.XS:
          return css`
            font-size: 14px;
            line-height: 20px;
          `;
        case GameItemSize.XXS:
        default:
          return css`
            font-size: 12px;
            line-height: 1.4;
          `;
      }
    }}
  }

  & > .play-btn__small {
    border: none;
    position: relative;
    display: inline-block;
    width: 48px;
    height: 48px;
    border-radius: 50%;
    background-color: ${({ theme }) => theme.secondaryColor};
    border: none;
    cursor: pointer;

    &::after {
      transition: 0.3s;
      content: '';
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      width: 24px;
      height: 24px;
      background-image: url(${playSmIcon});
    }

    @media (hover: hover) {
      &:hover {
        &::after {
          transform: translate(-50%, -50%) scale(1.1);
        }
      }
    }
  }

  & > .play-btn_container {
    width: 100%;

    ${({ size }) => {
      switch (size) {
        case GameItemSize.L:
        case GameItemSize.M:
          return css`
            max-width: 153px;
            height: 48px;
          `;
        case GameItemSize.S:
        case GameItemSize.XS:
          return css`
            max-width: 104px;
            height: 32px;
          `;
        case GameItemSize.XXS:
        default:
          return css`
            max-width: 100px;
            height: 28px;
          `;
      }
    }}

    .play-btn-content {
      display: flex;
      justify-content: center;
      align-items: center;
      text-transform: uppercase;
      font-weight: bold;
      border-radius: 40px;
      gap: 8px;

      ${({ primeGame }) =>
        primeGame &&
        css`
          max-height: 48px;
        `}

      ${({ size }) => {
        switch (size) {
          case GameItemSize.L:
          case GameItemSize.M:
            return css`
              font-size: 16px;
              line-height: 24px;
              & > img {
                width: 16px;
                height: 18px;
              }
            `;
          case GameItemSize.S:
          case GameItemSize.XS:
            return css`
              font-size: 14px;
              line-height: 16px;
              & > img {
                width: 10px;
                height: 12px;
              }
            `;
          case GameItemSize.XXS:
          default:
            return css`
              gap: 4px;
              font-size: 11px;
              & > img {
                width: 8px;
                height: 10px;
              }
            `;
        }
      }}
    }
  }

  & > .demo-btn_container {
    ${({ size }) => {
      switch (size) {
        case GameItemSize.L:
        case GameItemSize.M:
          return css`
            height: 40px;
          `;
        case GameItemSize.S:
        case GameItemSize.XS:
          return css`
            height: 28px;
          `;
        case GameItemSize.XXS:
        default:
          return css`
            height: 24px;
          `;
      }
    }}

    .demo-btn_content {
      font-weight: bold;
      font-size: 14px;
      letter-spacing: 0.01em;
      margin-top: 2px;
      color: ${({ theme }) => theme.whiteColor};

      ${({ size }) => {
        switch (size) {
          case GameItemSize.L:
          case GameItemSize.M:
            return css`
              line-height: 20px;
            `;
          case GameItemSize.S:
          case GameItemSize.XS:
            return css`
              line-height: 16px;
            `;
          case GameItemSize.XXS:
          default:
            return css`
              line-height: 14px;
              font-size: 11px;
            `;
        }
      }}
    }
  }
`;

export const ScPrimeGame = styled.div`
  width: 91%;
  padding: 24px;
  border-radius: 0 0 12px 12px;
  background-color: ${({ theme }) => theme.grayDarkColor};
  text-align: center;
  min-width: 0;

  p {
    font-size: 16px;
    line-height: 24px;
    font-weight: bold;
    letter-spacing: 0.01em;
    margin-bottom: 16px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  button {
    width: 86%;
    height: 40px;

    .text {
      .play-btn-content {
        font-size: 14px;
      }
    }

    @media (${device.desktop}) {
      width: 100%;

      &.fr-ca,
      &.fr-ch,
      &.de {
        .text {
          .play-btn-content {
            font-size: 11px;
          }
          img {
            width: 10%;
          }
        }
      }
    }

    @media (${device.mobileXS}) {
      width: 100%;
      .text {
        .play-btn-content {
          font-size: 11px;
          img {
            width: 12%;
          }
        }
      }
    }
  }

  @media (${device.tablet}) {
    padding: 24px 8px;
  }

  @media (${device.mobile}) {
    width: 100%;
    border-radius: 0 12px 12px 0;
    padding: 22px 24px;
  }

  @media (${device.mobileXS}) {
    padding: 20px 10px;
  }
`;

interface ScHearthBgWhiteProps {
  size?: GameItemSize;
  isFavorite?: boolean;
  isVisible?: boolean;
}

export const ScHearthBgWhite = styled.div<ScHearthBgWhiteProps>`
  position: absolute;
  width: 40px;
  transition: 0.3s;
  background: ${({ theme, isFavorite, isVisible }) =>
    isFavorite && !isVisible ? theme.whiteColor : 'none'};
  z-index: 3;
  aspect-ratio: 1/1;
  top: 0;
  right: 0;
  border-radius: 0 0 0 12px;

  ${({ size }) => {
    switch (size) {
      case GameItemSize.L:
      case GameItemSize.M:
        return css`
          width: 40px;
        `;
      case GameItemSize.S:
      case GameItemSize.XS:
      case GameItemSize.XXS:
      default:
        return css`
          width: 32px;
        `;
    }
  }}
`;
